import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, ArrowUpRight, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  contact: "",
  shots_done: 0,
  shots_pending: 0,
  amount_paid: 0,
  amount_due: 0,
  monthly_shoot_target: 0,
  on_hold: false,
  notes: "",
};

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [customRows, setCustomRows] = useState([]);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    const r = await api.get("/clients");
    setClients(r.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name) {
      toast.error("Name required");
      return;
    }
    const custom_fields = {};
    customRows.forEach((r) => { if (r.key) custom_fields[r.key] = r.value; });
    try {
      const r = await api.post("/clients", { ...form, custom_fields });
      setClients((p) => [r.data, ...p]);
      setOpen(false);
      setForm(EMPTY);
      setCustomRows([]);
      toast.success("Client added");
    } catch (e) {
      toast.error("Failed to add client");
    }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader
        eyebrow="Roster"
        title="Clients"
        description="Each client has their own dashboard with shots, payments, and posted videos."
        actions={
          isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="new-client-btn"
                  className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black"
                >
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  <span className="uppercase tracking-[0.15em] text-xs">New Client</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-neutral-950 border border-neutral-800 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">New Client</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="client-name-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Contact</Label>
                    <Input value={form.contact} placeholder="Email / Phone / Instagram" onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Shots Done</Label>
                    <Input type="number" value={form.shots_done} onChange={(e) => setForm({ ...form, shots_done: Number(e.target.value) })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Shots Pending</Label>
                    <Input type="number" value={form.shots_pending} onChange={(e) => setForm({ ...form, shots_pending: Number(e.target.value) })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Amount Paid</Label>
                    <Input type="number" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: Number(e.target.value) })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Amount Due</Label>
                    <Input type="number" value={form.amount_due} onChange={(e) => setForm({ ...form, amount_due: Number(e.target.value) })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Monthly Shoot Target</Label>
                    <Input type="number" value={form.monthly_shoot_target} onChange={(e) => setForm({ ...form, monthly_shoot_target: Number(e.target.value) })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="client-monthly-target" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Custom Fields</Label>
                      <Button size="sm" variant="ghost" onClick={() => setCustomRows((p) => [...p, { key: "", value: "" }])} className="h-7 rounded-none text-xs uppercase tracking-wider hover:bg-neutral-900">
                        <Plus className="w-3 h-3 mr-1" /> Add Field
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {customRows.map((r, i) => (
                        <div key={i} className="grid grid-cols-[1fr,1fr,auto] gap-2">
                          <Input placeholder="Field name" value={r.key} onChange={(e) => setCustomRows((p) => p.map((row, idx) => idx === i ? { ...row, key: e.target.value } : row))} className="rounded-none bg-neutral-900 border-neutral-800" />
                          <Input placeholder="Value" value={r.value} onChange={(e) => setCustomRows((p) => p.map((row, idx) => idx === i ? { ...row, value: e.target.value } : row))} className="rounded-none bg-neutral-900 border-neutral-800" />
                          <Button size="icon" variant="ghost" onClick={() => setCustomRows((p) => p.filter((_, idx) => idx !== i))} className="rounded-none hover:bg-neutral-900">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button onClick={submit} data-testid="client-submit-btn" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="px-6 lg:px-10 py-8">
        {clients.length === 0 ? (
          <div className="border border-neutral-900 px-6 py-16 text-center text-neutral-500">
            No clients yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900">
            {clients.map((c) => (
              <Link
                key={c.client_id}
                to={`/clients/${c.client_id}`}
                data-testid={`client-card-${c.client_id}`}
                className={`p-6 transition-colors group ${
                  c.on_hold
                    ? "bg-red-950/40 hover:bg-red-950/60 border-l-2 border-l-red-500"
                    : "bg-neutral-950 hover:bg-neutral-900/60"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
                      <span className={c.on_hold ? "text-red-400" : "text-neutral-500"}>Client</span>
                      {c.on_hold && (
                        <span className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] tracking-[0.15em]" data-testid="hold-badge">ON HOLD</span>
                      )}
                    </div>
                    <div className="font-heading text-xl font-semibold text-white">{c.name}</div>
                    {c.contact && <div className="text-xs text-neutral-500 mt-1">{c.contact}</div>}
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-neutral-600 group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-neutral-900">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Shots</div>
                    <div className="text-lg font-mono mt-1">{c.shots_done} / {c.shots_done + c.shots_pending}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Due</div>
                    <div className="text-lg font-mono mt-1 text-amber-400">₹ {Number(c.amount_due || 0).toLocaleString()}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
